import React, { useEffect, useState } from 'react';
import { Container, Form, Row, Col, Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import "../Styling/production.css";
import { useAuth } from '../Context/AuthContext';
import { Navigate,useNavigate } from 'react-router-dom';
import {
  getProductionPlans,
  getProductionTypes,
  getStatusIds,
  getCurrencies,
  submitProductionPlan,
} from '../Api/ProductionOrderApi';

const timeStringToDecimal = (timeStr) => {
  if (!timeStr) return 0;
  const parts = timeStr.split(':');
  const h = parseInt(parts[0] || '0', 10);
  const m = parseInt(parts[1] || '0', 10);
  return h + m / 60;
};

export default function ProductionOrderForm() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [types, setTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const navigate=useNavigate();

  const [form, setForm] = useState({
    productionPlanId: '',
    plannedStartDate: '',
    plannedStartTime: '',
    approxDurationText: '',
    approxCost: '',
    approxDurationTime: '',
    plannedEndDate: '',
    plannedEndTime: '',
    productionType: '',
    productionOrderStatusId: '',
    approxCostCurrency: '',
    orderId: '',
  });

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [p, t, s, c] = await Promise.all([
          getProductionPlans(),
          getProductionTypes(),
          getStatusIds(),
          getCurrencies(),
        ]);
        setPlans(p || []);
        setTypes(t || []);
        setStatuses(s || []);
        setCurrencies(c || []);
      } catch (err) {
        console.error('Failed to load dropdowns', err);
        toast.error('Failed to load lookup data');
      }
    };

    loadLookups();
  }, []);

  const handleChange = (key, value) => {
    setForm((f) => {
      const updated = { ...f, [key]: value };
      if (key === 'productionType' && value !== 'MO') {
        updated.orderId = '';
      }
      return updated;
    });
  };

  const resetForm = () => {
    setForm({
      productionPlanId: '',
      plannedStartDate: '',
      plannedStartTime: '',
      approxDurationText: '',
      approxCost: '',
      approxDurationTime: '',
      plannedEndDate: '',
      plannedEndTime: '',
      productionType: '',
      productionOrderStatusId: '',
      approxCostCurrency: '',
      orderId: '',
    });
    toast.info('Form reset');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const approxDurationDecimal = timeStringToDecimal(form.approxDurationTime);

      const payload = {
        productionPlanId: form.productionPlanId,
        plannedStartDate: form.plannedStartDate,
        plannedStartTime: form.plannedStartTime,
        approxDuration: form.approxDurationText,
        approxCost: parseFloat(form.approxCost) || 0,
        approxDurationDecimal,
        plannedEndDate: form.plannedEndDate,
        plannedEndTime: form.plannedEndTime,
        productionType: form.productionType,
        productionOrderStatusId: form.productionOrderStatusId,
        approxCostCurrency: form.currencyCode,
        orderId: form.orderId || null,
        createdBy: String(user?.userId),
        organisationId: user?.organisationId,
      };

      const res = await submitProductionPlan(payload);
      console.log(res.result);
      if (res!=null) {
        toast.success('Production Order created successfully!');
        navigate(`/createProduction-order/${res.result}`)
        resetForm();
      } else {
        toast.warn('Unexpected response status. Please verify.');
      }
    } catch (err) {
      console.error('Submit error', err);
      toast.error('Error creating production order. Please check console for details.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="ppf-container">
      <h2 className="ppf-title">Production Order</h2>
      <br />
      <Form onSubmit={handleSubmit} className="ppf-form">
        <Form.Group as={Row} className="mb-3" controlId="productionPlanId">
          <Form.Label sm={3} className="ppf-label">
            Production Plan
          </Form.Label>
          <Col md={3}>
            <Form.Select
              value={form.productionPlanId}
              onChange={(e) => handleChange('productionPlanId', e.target.value)}
              required
            >
              <option value="">-- Select Production Plan --</option>
              {plans.map((p) => (
                <option key={p.productionPlanId || p.id || JSON.stringify(p)} value={p.productionPlanId || p.id}>
                  {`${p.productionPlanId || p.id} - ${p.productionPlanDesc || p.productionPlanDescription || ''}`}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Form.Group>

        <Row className="mb-3">
          <Form.Group as={Col} sm={6} controlId="plannedStart">
            <Form.Label className="ppf-label">Planned Start</Form.Label>
            <Row>
              <Col>
                <Form.Control
                  type="date"
                  value={form.plannedStartDate}
                  onChange={(e) => handleChange('plannedStartDate', e.target.value)}
                  required
                />
              </Col>
              <Col>
                <Form.Control
                  type="time"
                  value={form.plannedStartTime}
                  onChange={(e) => handleChange('plannedStartTime', e.target.value)}
                  required
                />
              </Col>
            </Row>
          </Form.Group>

          <Form.Group as={Col} sm={6} controlId="plannedEnd">
            <Form.Label className="ppf-label">Planned End</Form.Label>
            <Row>
              <Col>
                <Form.Control
                  type="date"
                  value={form.plannedEndDate}
                  onChange={(e) => handleChange('plannedEndDate', e.target.value)}
                />
              </Col>
              <Col>
                <Form.Control
                  type="time"
                  value={form.plannedEndTime}
                  onChange={(e) => handleChange('plannedEndTime', e.target.value)}
                />
              </Col>
            </Row>
          </Form.Group>
        </Row>

        <Row className="mb-3">
          <Form.Group as={Col} sm={4} controlId="approxDurationText">
            <Form.Label className="ppf-label">Approx Duration</Form.Label>
            <Form.Control
              type="text"
              placeholder="Eg.24 (Data will be stored in hours)"
              value={form.approxDurationText}
              onChange={(e) => handleChange('approxDurationText', e.target.value)}
            />
          </Form.Group>

          <Form.Group as={Col} sm={4} controlId="approxCost">
            <Form.Label className="ppf-label">Approx Cost</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              value={form.approxCost}
              onChange={(e) => handleChange('approxCost', e.target.value)}
            />
          </Form.Group>
        </Row>

        <Row className="mb-3">
          <Form.Group as={Col} sm={4} controlId="productionType">
            <Form.Label className="ppf-label">Production Type</Form.Label>
            <Form.Select value={form.productionType} onChange={(e) => handleChange('productionType', e.target.value)}>
              <option value="">-- Select Production Type --</option>
              {types.map((t) => (
                <option key={t.productionType1 || JSON.stringify(t)} value={t.productionType1}>
                  {`${t.productionType1} - ${t.productionTypeDescription || t.description || ''}`}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group as={Col} sm={4} controlId="productionOrderStatusId">
            <Form.Label className="ppf-label">Production Order Status</Form.Label>
            <Form.Select
              value={form.productionOrderStatusId}
              onChange={(e) => handleChange('productionOrderStatusId', e.target.value)}
            >
              <option value="">-- Select Status --</option>
              {statuses.map((s) => (
                <option key={s.productionOrderStatusId || JSON.stringify(s)} value={s.productionOrderStatusId}>
                  {`${s.productionOrderStatusId} - ${s.productionOrderStatusDescription || s.description || ''}`}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group as={Col} sm={4} controlId="currencyCode">
            <Form.Label className="ppf-label">Currency</Form.Label>
            <Form.Select value={form.currencyCode} onChange={(e) => handleChange('currencyCode', e.target.value)}>
              <option value="">-- Select Currency --</option>
              {currencies.map((c) => (
                <option key={c.currencyCode || JSON.stringify(c)} value={c.currencyCode}>
                  {c.currencyCode}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Row>

        {form.productionType === 'MO' && (
          <Row className="mb-3">
            <Form.Group as={Col} sm={6} controlId="orderId">
              <Form.Label className="ppf-label">Order Id</Form.Label>
              <Form.Control
                type="text"
                value={form.orderId}
                onChange={(e) => handleChange('orderId', e.target.value)}
                required
                placeholder="Enter Order Id for MO"
              />
            </Form.Group>
          </Row>
        )}

        <Row className="ppf-actions">
          <Col>
            <Button variant="primary" type="submit" disabled={loading} className="me-2 ppf-btn">
              {loading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>

            <Button variant="secondary" type="button" onClick={resetForm} className="ppf-btn ppf-btn-secondary">
              Reset
            </Button>
          </Col>
        </Row>
      </Form>
    </Container>
  );
}
